/**
 * Module-level registry of the data keys offered by the `/` slash command. The
 * Suggestion plugin's `items` callback runs outside React, so the React layer
 * (see `useRegisterSlashDataKeys` in the editor provider) publishes the current
 * user data keys here and the extension reads them on demand.
 */

import type { DynamicNodeOption } from "../dynamic-node-context";

export type SlashDataKeyItem = DynamicNodeOption;

let _items: SlashDataKeyItem[] = [];

export const setSlashDataKeyItems = (items: SlashDataKeyItem[]): void => {
  _items = items;
};

export const getSlashDataKeyItems = (): SlashDataKeyItem[] => _items;

/**
 * One-shot signal used to auto-open a freshly inserted node's editor bubble. The
 * slash command inserts an empty node and raises this flag for its kind; the next
 * empty node view of that kind to mount consumes it and opens its picker. Crossing
 * the editor<->NodeView React boundary via a module flag avoids storing transient
 * UI state as a node attribute (keeps the node stateless / backend-schema-safe).
 */
export type DynamicNodeKind = "datakey" | "context" | "did" | "conditional";

const _pendingEditorOpen: Record<DynamicNodeKind, boolean> = {
  datakey: false,
  context: false,
  did: false,
  conditional: false,
};

export const requestEditorOpen = (kind: DynamicNodeKind): void => {
  _pendingEditorOpen[kind] = true;
};

export const consumeEditorOpen = (kind: DynamicNodeKind): boolean => {
  if (!_pendingEditorOpen[kind]) {
    return false;
  }
  _pendingEditorOpen[kind] = false;
  return true;
};
